import equal from 'fast-deep-equal';
import { call, delay, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import spacetime from 'spacetime';
import type { ActionType } from 'typesafe-actions';

import { UserRoleEnum } from 'APIPackage/AuthModule/enums/UserRoleEnum';
import { KnmEditStatusesEnum } from 'APIPackage/KnmModule/enums/KnmEditStatusesEnum';
import { IKnmDataViolation } from 'APIPackage/KnmModule/interfaces/IKnmDataViolation';
import { IKnmEditor } from 'APIPackage/KnmModule/interfaces/IKnmEditor';
import {
  fetchKnmEditorsByKnmId,
  fetchKnmHistory,
  fetchKnmSignedHistory,
  fetchUserWriteAccessToKnmById,
} from 'APIPackage/KnmModule/services/knmAdditionalDataApi';
import { fetchKnmViolations } from 'APIPackage/KnmModule/services/knmViolationsApi';
import { AuthActionsEnum } from 'AuthPackage/MainModule/enums/AuthActionsEnum';
import {
  getUserRoleIsProsecutorOrSuperProsecutor,
  getUserRoleIsSupervisor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { REFRESH_DELAY } from 'CorePackage/MainModule/constants/refreshDelay';
import { tzMoscow } from 'CorePackage/MainModule/constants/timeZone';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { NEW_KNM_ID } from 'CorePackage/MainModule/routes/routes';
import { addNoticeAction, removeNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { IKnmHistoryItem } from 'APIPackage/KnmModule/interfaces/IKnmHistoryItem';
import { KnmHistoryOperationsEnum } from 'APIPackage/KnmModule/enums/KnmHistoryOperationsEnum';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_ISO_FNS, DATE_FORMAT_LONG_MINUTE_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { knmHistoryFieldOperationsMap } from 'KnmPackage/KnmHistoriesModule/constants/knmHistoryFieldOperationsMap';
import { knmHistoryFields } from 'KnmPackage/KnmHistoriesModule/constants/knmHistoryFields';
import { KNM_HISTORY_FIELD_TEXT_PREFIX } from 'KnmPackage/KnmHistoriesModule/constants/knmHistoryFieldTextPrefix';
import { IKnmHistoryItemChanges } from 'KnmPackage/KnmHistoriesModule/interfaces/IKnmHistoryItemChanges';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';
import { IResponseError } from 'APIPackage/MainModule/interfaces/IResponseError';

import { fetchKnmSaga } from './knmFetchKnmSaga';
import {
  getKnmData,
  getKnmDataId,
  getKnmEditorTime,
  getKnmEditStatus,
  getKnmFormRef,
  getKnmHasReasonsImpossibility,
  getKnmHistoryChangesIsOn,
  getKnmId,
  getKnmIsEditorsUpdateEnabled,
  getKnmPrevEditors,
  getKnmSystemType,
  getKnmUrlId,
} from '../selectors/knmSelectors';
import { KnmSystemTypesEnum } from '../enums/KnmSystemTypesEnum';
import { KnmActionsEnum } from '../enums/KnmActionsEnum';
import { KnmEditBlockedNotification } from '../components/KnmEditBlockedNotification';
import { convertKnmUrlIdToId } from '../common/convertKnmUrlIdToId';
import {
  knmGetSignedHistoryChanges,
  knmSetEditorTime,
  knmSetEditStatus,
  knmSetHistoryChanges,
  knmSetHistoryChangesIsLoading,
  knmSetPrevKnmEditors,
  knmSetSignedHistoryChanges,
  knmViolationsGetFailure,
  knmViolationsGetRequest,
  knmViolationsGetSuccess,
} from '../actions/knmActions';
import { uuidRegExp } from '../constants/uuidRegExp';
import { ProfKindCodesEnum } from '../../../APIPackage/DictionariesModule/enums/ProfKindCodesEnum';
import { doubleIdRegExp } from '../constants/doubleIdRegExp';
import { LawInterfacesEnum } from '../../../CorePackage/MainModule/enums/LawInterfacesEnum';
import { getLawInterface } from '../../../CorePackage/MainModule/reducers/coreReducer';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IKnm } from '../../../APIPackage/KnmModule/interfaces/IKnm';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';

const KNM_EDIT_TIMEOUT_MINUTES_NUMBER = 5;

export function* watchKnmBackgroundDataSaga() {
  yield takeLatest(KnmActionsEnum.KNM_INIT_EDITORS_UPDATE, initKnmEditorsUpdateSaga);
  yield takeLatest(
    [
      AuthActionsEnum.SET_USER,
      AuthActionsEnum.CLEAR_USER,
      KnmActionsEnum.KNM_SET_SYSTEM_TYPE,
      KnmActionsEnum.KNM_SET_RAW_DATA,
      KnmActionsEnum.KNM_GET_SUCCESS,
      KnmActionsEnum.KNM_UPDATE_SUCCESS,
    ],
    knmViolationsGetHandler,
  );
  yield takeEvery(KnmActionsEnum.KNM_GET_HISTORY_CHANGES, knmGetHistoryChangesSaga);
  yield takeEvery(KnmActionsEnum.KNM_GET_SIGNED_HISTORY_CHANGES, knmGetSignedHistoryChangesSaga);
  yield call(watchUrlIdChangeSaga);
}

function* updateFetchKnmEditRestriction(knmId: number, shouldSkipWriteAccessCheck?: boolean) {
  try {
    const state: IRootState = yield select();

    if (typeof knmId !== 'number') {
      return;
    }
    const oldKnmEditStatus: KnmEditStatusesEnum | undefined = getKnmEditStatus(state);

    if (oldKnmEditStatus === KnmEditStatusesEnum.READ_ONLY) {
      return;
    }

    if (!shouldSkipWriteAccessCheck) {
      const isErknm: boolean = yield getLawInterface(state) === LawInterfacesEnum.FZ_248;
      const isWriteAccess: boolean = isErknm ? yield fetchUserWriteAccessToKnmById(knmId) : true;

      if (!isWriteAccess) {
        yield put(knmSetEditStatus(KnmEditStatusesEnum.READ_ONLY));

        return;
      }
    }

    const newKnmEditors: IKnmEditor[] = yield fetchKnmEditorsByKnmId(knmId);
    const isSupervisor = getUserRoleIsSupervisor(state);
    const prevKnmEditors: IKnmEditor[] | undefined = getKnmPrevEditors(state);
    const isEditByAnotherRoleProsecutor =
      isSupervisor &&
      newKnmEditors.some(
        item => item.roleCode !== UserRoleEnum.PROSECUTOR && item.roleCode !== UserRoleEnum.SUPERIOR_PROSECUTOR,
      );
    let newKnmEditStatus: KnmEditStatusesEnum | undefined = KnmEditStatusesEnum.ENABLED;
    const filteredNewKnmEditors = newKnmEditors.filter(
      item =>
        item.roleCode !== UserRoleEnum.PROSECUTOR &&
        item.roleCode !== UserRoleEnum.SUPERIOR_PROSECUTOR &&
        !prevKnmEditors?.find(prevItem => prevItem.userName === item.userName),
    );
    const userDate = spacetime.now(tzMoscow);
    const editorTime = getKnmEditorTime(state);
    const newPrevKnmEditors = prevKnmEditors ? [...prevKnmEditors, ...filteredNewKnmEditors] : filteredNewKnmEditors;

    if (!oldKnmEditStatus && isEditByAnotherRoleProsecutor && newPrevKnmEditors.length) {
      if (prevKnmEditors === undefined) {
        yield put(knmSetEditorTime(userDate));
        newKnmEditStatus = KnmEditStatusesEnum.WAITING_ENABLE;
      }
    }
    if (oldKnmEditStatus === KnmEditStatusesEnum.WAITING_ENABLE) {
      if (editorTime && userDate.isAfter(editorTime.add(KNM_EDIT_TIMEOUT_MINUTES_NUMBER, 'minute'))) {
        newKnmEditStatus = KnmEditStatusesEnum.ENABLED;
        yield put(knmSetEditorTime(undefined));
        yield put(removeNoticeAction(MessageTypesEnum.KNM_EDIT_RESTRICTION_WAITING_ENABLE));
        yield put(removeNoticeAction(MessageTypesEnum.KNM_EDITORS));
      } else {
        newKnmEditStatus = KnmEditStatusesEnum.WAITING_ENABLE;
      }
    }

    if (
      oldKnmEditStatus !== KnmEditStatusesEnum.WAITING_ENABLE &&
      oldKnmEditStatus &&
      prevKnmEditors &&
      newPrevKnmEditors.length &&
      !equal(newPrevKnmEditors, prevKnmEditors)
    ) {
      newKnmEditStatus = KnmEditStatusesEnum.ENABLED_WAITING_OTHER;
    }

    const fullNames = newKnmEditors
      .filter(item => item.roleCode !== UserRoleEnum.PROSECUTOR && item.roleCode !== UserRoleEnum.SUPERIOR_PROSECUTOR)
      .map(item => item.fullName);

    if (!equal(newPrevKnmEditors, prevKnmEditors)) {
      yield put(removeNoticeAction(MessageTypesEnum.KNM_EDITORS));
      yield put(removeNoticeAction(MessageTypesEnum.KNM_EDIT_RESTRICTION_ENABLED_WAITING_OTHER));
      yield put(removeNoticeAction(MessageTypesEnum.KNM_EDIT_RESTRICTION_WAITING_ENABLE));

      if (newKnmEditStatus === KnmEditStatusesEnum.ENABLED_WAITING_OTHER && isEditByAnotherRoleProsecutor) {
        const isPm: boolean = getKnmSystemType(state) === KnmSystemTypesEnum.PM;

        yield put(
          addNoticeAction({
            id: MessageTypesEnum.KNM_EDITORS,
            noticeType: NoticeTypesEnum.WARNING,
            messageType: MessageTypesEnum.KNM_EDITORS,
            renderContent: <KnmEditBlockedNotification names={fullNames} />,
          }),
        );
        yield put(
          addNoticeAction({
            id: MessageTypesEnum.KNM_EDIT_RESTRICTION_ENABLED_WAITING_OTHER,
            noticeType: NoticeTypesEnum.WARNING,
            messageType: MessageTypesEnum.KNM_EDIT_RESTRICTION_ENABLED_WAITING_OTHER,
            renderContent: `Данный паспорт ${
              isPm ? 'ПМ' : 'КНМ'
            } открыл на редактирование другой пользователь. Пожалуйста, сохраните изменения, чтобы не потерять их`,
          }),
        );
      }
      if (newKnmEditStatus === KnmEditStatusesEnum.WAITING_ENABLE) {
        const isPm: boolean = getKnmSystemType(state) === KnmSystemTypesEnum.PM;

        yield put(
          addNoticeAction({
            id: MessageTypesEnum.KNM_EDITORS,
            noticeType: NoticeTypesEnum.WARNING,
            messageType: MessageTypesEnum.KNM_EDITORS,
            renderContent: <KnmEditBlockedNotification names={fullNames} />,
          }),
        );
        if (oldKnmEditStatus !== KnmEditStatusesEnum.WAITING_ENABLE) {
          yield put(
            addNoticeAction({
              id: MessageTypesEnum.KNM_EDIT_RESTRICTION_WAITING_ENABLE,
              noticeType: NoticeTypesEnum.WARNING,
              messageType: MessageTypesEnum.KNM_EDIT_RESTRICTION_WAITING_ENABLE,
              renderContent: <KnmEditBlockedNotification isPm={isPm} />,
            }),
          );
        }
      }
    }
    yield put(knmSetPrevKnmEditors(newPrevKnmEditors));
    if (newKnmEditStatus !== oldKnmEditStatus) {
      yield put(knmSetEditStatus(newKnmEditStatus));
    }
  } catch (err) {
    const currentErr = err as IResponseError;

    if (currentErr.status) {
      yield put(knmSetEditStatus(KnmEditStatusesEnum.ENABLED));
    }
  } finally {
    yield delay(REFRESH_DELAY);
  }
}

function* initKnmEditorsUpdateSaga() {
  let knmId: number | undefined = yield select(getKnmId);
  let hasCheckedWriteAccess = false;

  while (typeof knmId === 'number') {
    knmId = yield select(getKnmId);

    if (typeof knmId !== 'number') {
      break;
    }
    yield updateFetchKnmEditRestriction(knmId, hasCheckedWriteAccess);
    hasCheckedWriteAccess = true;
  }
}

function* handleUrlIdChangeSaga(prevUrlId: string | undefined, nextUrlId: string | undefined) {
  const isKnmEditorsUpdateEnabled: boolean = yield select(getKnmIsEditorsUpdateEnabled);
  const nextId = convertKnmUrlIdToId(nextUrlId);

  if (nextUrlId === NEW_KNM_ID) {
    yield put(knmSetEditStatus(KnmEditStatusesEnum.ENABLED));
  } else if (typeof nextId === 'number') {
    yield fetchKnmSaga(nextId, isKnmEditorsUpdateEnabled ? initKnmEditorsUpdateSaga : undefined);
  }
}

function* watchUrlIdChangeSaga() {
  let prevUrlId: string | undefined;

  while (true) {
    yield take(KnmActionsEnum.KNM_SET_URL_ID);
    const nextUrlId: string | undefined = yield select(getKnmUrlId);

    if (nextUrlId !== prevUrlId) {
      yield handleUrlIdChangeSaga(prevUrlId, nextUrlId);
    }
    prevUrlId = nextUrlId;
  }
}

function* fetchKnmViolationsSaga(knmId: number) {
  function* funcTry() {
    yield put(knmViolationsGetRequest());
    const knmViolations: IKnmDataViolation[] = yield fetchKnmViolations(knmId);

    yield put(knmViolationsGetSuccess(knmViolations));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(knmViolationsGetFailure());
    },
  });
}

function* knmViolationsGetHandler() {
  const state: IRootState = yield select();
  const knmSystemType = getKnmSystemType(state);
  const isProsecutorOrSuperProsecutor: boolean | undefined = getUserRoleIsProsecutorOrSuperProsecutor(state);
  const knmId = getKnmDataId(state);

  if (isProsecutorOrSuperProsecutor && knmSystemType === KnmSystemTypesEnum.ERP && typeof knmId === 'number') {
    yield fetchKnmViolationsSaga(knmId);
  }
}

function* knmGetHistoryChangesSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const id = getKnmDataId(state);
    const isOn = getKnmHistoryChangesIsOn(state);

    if (id && isOn) {
      yield put(knmSetHistoryChangesIsLoading(true));
      const response: IKnmHistoryItem[] = yield fetchKnmHistory(id);

      yield knmFormedHistoryChanges(response);
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcFinally() {
      yield put(knmSetHistoryChangesIsLoading(false));
    },
  });
}

function* knmGetSignedHistoryChangesSaga(action: ActionType<typeof knmGetSignedHistoryChanges>) {
  const { startDateTime, endDateTime } = action.payload;

  function* funcTry() {
    const state: IRootState = yield select();
    const id = getKnmDataId(state);

    if (id) {
      yield put(knmSetHistoryChangesIsLoading(true));
      const response: IKnmHistoryItem[] = yield fetchKnmSignedHistory(id, startDateTime, endDateTime);

      yield knmFormedHistoryChanges(response, true);
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcFinally() {
      yield put(knmSetHistoryChangesIsLoading(false));
    },
  });
}

function* knmFormedHistoryChanges(knmHistory: IKnmHistoryItem[], isSignedHistory?: boolean) {
  const state: IRootState = yield select();
  const knmData: IKnm | undefined = yield select(getKnmData);
  const settings: IAdminSetting[] | undefined = yield select(getAdminFetchSettings);
  const isReasonsImpossibility = getKnmHasReasonsImpossibility(state);
  const isErp: boolean = getKnmSystemType(state) === KnmSystemTypesEnum.ERP;
  const isPm: boolean = getKnmSystemType(state) === KnmSystemTypesEnum.PM;
  const isErknm: boolean = getKnmSystemType(state) === KnmSystemTypesEnum.ERKNM;
  const isPmTypeOP: boolean =
    isPm && getKnmFormRef(state)?.current.getFieldState('pmType')?.value?.code === ProfKindCodesEnum.OP;
  const newKnmHistoryChanges: IKnmHistoryItemChanges[] = [];
  const newKnmExecutionHistoryChanges: IKnmHistoryItemChanges[] = [];

  const isLaterThanLegallyInsignificantStatementSetting = isLaterThanAdminSetting(
    knmData?.created,
    settings,
    AdminSettingCodesEnum.LEGALLY_INSIGNIFICANT_STATEMENT_STARTS_FROM,
  );

  knmHistory?.forEach(knmHistoryItem => {
    const title = `Обновлено ${knmHistoryItem.user?.fullName} (${knmHistoryItem.user?.username}) ${formatDate({
      date: knmHistoryItem.utcTimestamp,
      dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
      parseFormat: DATE_FORMAT_ISO_FNS,
    })} (МСК)`;
    const historyChangesText: Array<string> = [];
    const executionHistoryChangesText: Array<string> = [];

    const hasRiskIndicatorShortTitle =
      knmHistoryItem.changes.findIndex(history => history.field.indexOf('/riskIndicator/shortTitle') >= 0) >= 0;

    knmHistoryItem.changes.forEach(changesItem => {
      const newField = changesItem.field
        .split('/')
        .map(item =>
          uuidRegExp.test(item) || (item !== '' && Number.isInteger(Number(item))) || doubleIdRegExp.test(item)
            ? '{id}'
            : item,
        )
        .join('/');

      let isAdditionalInfo = false;

      if (newField === '/objects/{id}/result/additionalInfo/{id}/information') {
        isAdditionalInfo = true;
      }
      const field = knmHistoryFields[newField];

      if (!field?.active) {
        return;
      }
      let fieldName = '';

      if (isAdditionalInfo) {
        const additionalInfoTypePath = changesItem.field.replace('information', 'resultInfoType/name');

        knmHistory?.some(hItem => {
          const resChangesItem = hItem.changes.find(
            searchChangesItem => searchChangesItem.field === additionalInfoTypePath,
          );

          if (resChangesItem) {
            fieldName = typeof resChangesItem.newValue === 'string' ? resChangesItem.newValue : '';

            return true;
          }

          return false;
        });
      } else {
        fieldName = (isPm ? field.name.split('КНМ').join('ПМ') : field.name) || newField;
      }
      if (isPm && newField === '/type/name') {
        fieldName = 'Основание проведения ПМ';
      }
      if (isPmTypeOP && newField === '/startDate') {
        fieldName = 'Дата объявления предостережения';
      }
      if (isErknm && newField === '/type/name') {
        fieldName = 'Характер КНМ';
      }
      if (isErknm && newField === '/durationHours') {
        fieldName = 'Срок непосредственного взаимодействия (часов)';
      }
      if (
        isPm &&
        isPmTypeOP &&
        newField === '/contentWarning/attachments/{id}/fileName' &&
        isLaterThanLegallyInsignificantStatementSetting
      ) {
        fieldName = 'Выписка';
      }

      if (newField === '/organization/{id}/resultDecisions/{id}/dateDecision') {
        const kindDecisionPath = changesItem.field.replace(/dateDecision$/, 'kindDecision/name');
        const kindDecisionName = [knmHistoryItem, ...knmHistory]
          .flatMap(item => item.changes)
          .find(item => item.field === kindDecisionPath && typeof item.newValue === 'string')?.newValue;

        if (
          typeof kindDecisionName === 'string' &&
          kindDecisionName.startsWith('Предписание о приостановке реализации продукции')
        ) {
          fieldName = 'Дата выдачи предписания';
        }
      }

      if (newField === '/reasons/{id}/riskIndicators/{id}/riskIndicator/title' && hasRiskIndicatorShortTitle) {
        return;
      }

      const { oldValue, newValue } = changesItem;
      let oldValueText = oldValue;
      let newValueText = newValue;
      let valuesText: undefined | string;
      let changeText: undefined | string;

      if (field.renderFunc) {
        const { renderedOldValue, renderedNewValue, renderedValuesText, renderedChangeText } = field.renderFunc(
          changesItem,
          {
            isErp,
            isReasonsImpossibility,
          },
        );

        oldValueText = renderedOldValue;
        newValueText = renderedNewValue;
        valuesText = renderedValuesText;
        changeText = renderedChangeText;
      }

      if (valuesText === undefined) {
        valuesText = '';

        if (changesItem.operation === KnmHistoryOperationsEnum.INSERT) {
          valuesText = `"${newValueText}"`;
        } else if (changesItem.operation === KnmHistoryOperationsEnum.UPDATE) {
          valuesText = `${oldValueText ? `с "${oldValueText}" ` : ''}на "${newValueText}"`;
        } else if (
          changesItem.operation === KnmHistoryOperationsEnum.DELETE &&
          [
            '/organization/{id}/okveds/{id}/code',
            '/organization/{id}/okveds/{id}/name',
            '/subject/okveds/{id}/code',
            '/subject/okveds/{id}/name',
          ].includes(newField)
        ) {
          valuesText = `"${oldValue}"`;
        }
      }

      if (changeText === undefined) {
        changeText = `${KNM_HISTORY_FIELD_TEXT_PREFIX} "${fieldName}" ${
          knmHistoryFieldOperationsMap[changesItem.operation]
        } ${valuesText}`;
      }

      if (newField === '/status/name') {
        executionHistoryChangesText.push(changeText);
      } else {
        historyChangesText.push(changeText);
      }
    });
    if (historyChangesText.length) {
      newKnmHistoryChanges.push({
        title,
        changesText: historyChangesText,
        eventDate: knmHistoryItem.utcTimestamp,
      });
    }
    if (executionHistoryChangesText.length) {
      newKnmExecutionHistoryChanges.push({
        title,
        changesText: executionHistoryChangesText,
        eventDate: knmHistoryItem.utcTimestamp,
      });
    }
  });

  if (isSignedHistory) {
    yield put(
      knmSetSignedHistoryChanges({
        main: newKnmHistoryChanges,
        execution: newKnmExecutionHistoryChanges,
      }),
    );
  } else {
    yield put(
      knmSetHistoryChanges({
        main: newKnmHistoryChanges,
        execution: newKnmExecutionHistoryChanges,
      }),
    );
  }
}
